import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { EntityPathOption } from '../types';

export interface SystemEntityPathOptionsState {
  options: EntityPathOption[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (path: string, label?: string | null) => Promise<EntityPathOption>;
  updateLabel: (path: string, label: string | null) => Promise<EntityPathOption>;
  remove: (path: string) => Promise<void>;
  reassign: (from: string, to: string) => Promise<ReassignResult>;
}

// FS-910: the server moves devices on exactly `from`, never its sub-paths.
export interface ReassignResult {
  moved: number;
  from: string;
  to: string;
  match: 'exact';
}

// Plan 83: CRUD against /system/entity-path-options (gated by system_admin).
// Used by the System page. Separate from useEntityPathOptions (which hits
// the read-only /devices projection gated by write_devices).
export function useSystemEntityPathOptions(): SystemEntityPathOptionsState {
  const [options, setOptions] = useState<EntityPathOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const baseUrl = `${config.devicesApiUrl}/system/entity-path-options`;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<EntityPathOption[]>(baseUrl);
      setOptions(res.data);
    } catch (err) {
      console.error('Error fetching system entity path options:', err);
      setError('Failed to load entity path options');
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(async (path: string, label?: string | null) => {
    const res = await axiosInstance.post<EntityPathOption>(baseUrl, { path, label: label ?? null });
    await refresh();
    return res.data;
  }, [baseUrl, refresh]);

  const updateLabel = useCallback(async (path: string, label: string | null) => {
    const res = await axiosInstance.put<EntityPathOption>(
      `${baseUrl}/${encodeURIComponent(path)}`,
      { label },
    );
    await refresh();
    return res.data;
  }, [baseUrl, refresh]);

  const remove = useCallback(async (path: string) => {
    await axiosInstance.delete(`${baseUrl}/${encodeURIComponent(path)}`);
    await refresh();
  }, [baseUrl, refresh]);

  // Bulk-move every device on exactly `from` to `to`. Returns server payload.
  const reassign = useCallback(async (from: string, to: string) => {
    const res = await axiosInstance.post<ReassignResult>(
      `${baseUrl}/${encodeURIComponent(from)}/reassign`,
      { to },
    );
    await refresh();
    return res.data;
  }, [baseUrl, refresh]);

  return { options, loading, error, refresh, create, updateLabel, remove, reassign };
}
